
import { TabStyles } from '@tableau/widgets';
import React from 'react';

import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { FormikCheckbox } from '~src/commonInfra/components/FormikCheckbox';
import { openReactConfirmationDialog,ReactConfirmationDialogBodyProps } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { useSelector } from '~src/settings/general-settings/utils';

import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export const AUTO_QUALITY_MONITORING_SECTION_TEST_ID = 'auto-quality-monitoring-setting-section';

export const AUTO_FLOW_RUN_MONITORING_FIELD = 'settings.autoFlowRunMonitoringEnabled';
export const AUTO_FLOW_RUN_MONITORING_CHECKBOX_TEST_ID = 'auto-flow-run-monitoring-setting-checkbox';

export const AUTO_EXTRACT_REFRESH_MONITORING_FIELD = 'settings.autoExtractRefreshMonitoringEnabled';
export const AUTO_EXTRACT_REFRESH_MONITORING_CHECKBOX_TEST_ID = 'auto-extract-refresh-monitoring-setting-checkbox';

export type AutoQualityMonitoringSectionProps = SiteSettingsForSiteAdminProps<'autoFlowRunMonitoringEnabled' | 'autoExtractRefreshMonitoringEnabled'>;

export function AutoQualityMonitoringSection() {
  const catalogingEnabledOnSite = useSelector(SessionSiteSelectors.isCatalogingOnSiteEnabled);
  const isUserAdmin = useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  const autoQualityMonitoringEnabled = FeatureFlags.autoQualityMonitoringEnabled;

  return (
    catalogingEnabledOnSite && autoQualityMonitoringEnabled && isUserAdmin ?
    <SettingsSection
      title={Messages.autoQualityMonitoringSettingsSectionHeader()}
      testId={AUTO_QUALITY_MONITORING_SECTION_TEST_ID}>

      <FormikCheckbox name={AUTO_EXTRACT_REFRESH_MONITORING_FIELD}
                      label={Messages.autoExtractRefreshMonitoringSettingLabel()}
                      testId={AUTO_EXTRACT_REFRESH_MONITORING_CHECKBOX_TEST_ID}
                      style={{marginBottom: TabStyles.Spacing.DefaultSpace}}
                      beforeChange={openConfirmationDialog}/>

      <FormikCheckbox name={AUTO_FLOW_RUN_MONITORING_FIELD}
                      label={Messages.autoFlowRunMonitoringSettingLabel()}
                      testId={AUTO_FLOW_RUN_MONITORING_CHECKBOX_TEST_ID}
                      style={{marginBottom: TabStyles.Spacing.DefaultSpace}}
                      beforeChange={openConfirmationDialog}/>
    </SettingsSection>
    : null
  );
}

function getMonitoringConfirmationDialogText(turnOffMonitoring: boolean, monitoringType: string, monitoringFailures: string){
  if (turnOffMonitoring){
    return Messages.autoQualityMonitoringTurnOffDialogMessage({
      MONITORING_TYPE: monitoringType
    });
  }
  return Messages.autoQualityMonitoringTurnOnDialogMessage({
    MONITORING_TYPE: monitoringType,
    MONITORING_FAILURE_PLURAL: monitoringFailures
  });
}

function openConfirmationDialog(event: React.ChangeEvent<HTMLInputElement>) {
  const turnOffMonitoring = event.target.value === 'true';

  const isFlowMonitoring = event.target.name === AUTO_FLOW_RUN_MONITORING_FIELD;

  const monitoringType = isFlowMonitoring ? Messages.flowRunMonitoringLower()
  : Messages.extractRefreshMonitoringLower();

  const monitoringFailures = isFlowMonitoring ? Messages.flowRunFailurePlural()
  : Messages.extractRefreshFailuresPlural();

  const bullet1 = turnOffMonitoring ? Messages.autoQualityMonitoringTurnOffBullet1({
    MONITORING_TYPE: monitoringType,
    MONITORING_FAILURE_PLURAL: monitoringFailures
  })
  : Messages.autoQualityMonitoringTurnOnBullet1({
    MONITORING_TYPE: monitoringType,
  });

  const bullet2 = turnOffMonitoring ? Messages.autoQualityMonitoringTurnOffBullet2()
  : Messages.autoQualityMonitoringTurnOnBullet2({
    MONITORING_TYPE: monitoringType,
  });

  function getMonitoringConfirmationDialogBody(props: ReactConfirmationDialogBodyProps): JSX.Element {
    return (
      <div>
      <p>{props.message}</p>
      <ul>
        <li>{bullet1}</li>
        <li>{bullet2}</li>
      </ul>
      </div>
    );
  }

  const dialogPromiseResult = openReactConfirmationDialog({
    title: isFlowMonitoring
      ? Messages.autoFlowRunMonitoringConfirmationDialogHeader()
      : Messages.autoExtractRefreshMonitoringConfirmationDialogHeader(),
    message: getMonitoringConfirmationDialogText(turnOffMonitoring, monitoringType, monitoringFailures),
    actionName: Messages.proceed(),
    action: () => Promise.resolve(),
    dialogWidth: TabDialogWidth.width500px,
    paramsForAction: null,
    isActionDelete: turnOffMonitoring,
    bodyComponent: getMonitoringConfirmationDialogBody
  }).result;
  return dialogPromiseResult;
}

export const AutoQualityMonitoringSectionSpec = componentRegistryEntry({
    name: 'AutoQualityMonitoringSection',
    component: AutoQualityMonitoringSection
});
